/**
 * TargetSummaryCard — Phase 2.2B Session 3
 * Active target display. Used by both staff dashboard and athlete self-view.
 */
import React from 'react';
import { Target as TargetIcon, Sparkles, User, Pencil } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const fmtInt = (v, s = '') => v === null || v === undefined ? '—' : `${Math.round(Number(v))}${s}`;
const fmt1 = (v, s = '') => v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}${s}`;

const StatBox = ({ label, value, accent = 'text-zinc-100', testid }) => (
  <div className="bg-zinc-950/50 border border-zinc-800 rounded p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <div className={`text-xl font-bold ${accent}`}>{value}</div>
  </div>
);

const TargetSummaryCard = ({ target, weightKg, canEdit = false, onEdit }) => {
  const { language } = useLanguage();
  const final = target?.final || {};
  const protPerKg = (final.protein_g && weightKg) ? final.protein_g / weightKg : null;
  const carbsPerKg = (final.carbs_g && weightKg) ? final.carbs_g / weightKg : null;
  const fatsPerKg = (final.fats_g && weightKg) ? final.fats_g / weightKg : null;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="target-summary-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TargetIcon className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-zinc-100">
            {language === 'IT' ? 'Target attivo' : 'Active target'}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {target && (
            <>
              <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/40 text-xs" data-testid="target-goal-badge">
                {target.goal}
              </Badge>
              <Badge
                className={target.mode === 'AUTO'
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-xs'
                  : 'bg-zinc-700 text-zinc-200 border-zinc-600 text-xs'}
                data-testid="target-source-badge"
              >
                {target.mode === 'AUTO' ? <Sparkles className="w-3 h-3 mr-1 inline" /> : <User className="w-3 h-3 mr-1 inline" />}
                {target.mode}
              </Badge>
              <span className="text-xs text-zinc-500" data-testid="target-effective-from">{target.effective_from}</span>
            </>
          )}
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={onEdit} data-testid="target-summary-edit"
              className="text-zinc-400 hover:text-cyan-300">
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {language === 'IT' ? 'Nuovo target' : 'New target'}
            </Button>
          )}
        </div>
      </div>

      {!target ? (
        <div className="text-sm text-zinc-500 py-4 text-center" data-testid="target-empty">
          {language === 'IT'
            ? canEdit ? 'Nessun target attivo. Crea il primo target dal pulsante.' : 'Nessun target attivo.'
            : canEdit ? 'No active target. Create the first one with the button above.' : 'No active target.'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox testid="target-summary-calories" label={language === 'IT' ? 'Calorie' : 'Calories'}
              value={fmtInt(final.calories, ' kcal')} accent="text-cyan-300" />
            <StatBox testid="target-summary-protein" label={language === 'IT' ? 'Proteine' : 'Protein'}
              value={fmt1(final.protein_g, ' g')} />
            <StatBox testid="target-summary-carbs" label={language === 'IT' ? 'Carboidrati' : 'Carbs'}
              value={fmt1(final.carbs_g, ' g')} />
            <StatBox testid="target-summary-fats" label={language === 'IT' ? 'Grassi' : 'Fats'}
              value={fmt1(final.fats_g, ' g')} />
          </div>
          {weightKg && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] text-zinc-500" data-testid="target-summary-per-kg">
              <span />
              <span>{protPerKg !== null ? `${fmt1(protPerKg)} g/kg` : ''}</span>
              <span>{carbsPerKg !== null ? `${fmt1(carbsPerKg)} g/kg` : ''}</span>
              <span>{fatsPerKg !== null ? `${fmt1(fatsPerKg)} g/kg` : ''}</span>
            </div>
          )}
          {target.rationale && (
            <div className="mt-3 text-xs text-zinc-400 italic" data-testid="target-summary-rationale">
              &ldquo;{target.rationale}&rdquo;
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TargetSummaryCard;
